import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { CompanyProfileType } from '../../../types/company';
import CountryDropdown from '../../../components/dropdown/country';
import StateDropdown from '../../../components/dropdown/states';
import CityDropdown from '../../../components/dropdown/city';

interface EditCompanyModalProps {
    show: boolean;
    loading: boolean;
    setShow: (show: boolean) => void;
    data: Partial<CompanyProfileType> & {
        createdDate?: string;
        registeredBy?: string;
        uploadedDate?: string;
        isLatestDocumentEsigned?: boolean;
    };
    onSave: (updated: any) => void;
}

const EditCompanyModal: React.FC<EditCompanyModalProps> = ({ show, setShow, data, onSave, loading }) => {
    const [form, setForm] = useState<any>(data);
    const [iso2CountryString, setIso2CountryString] = useState('');
    const [iso2StateString, setIso2StateString] = useState('');
    const [stateDisabled, setStateDisabled] = useState(true);
    const [cityDisabled, setCityDisabled] = useState(true);

    useEffect(() => {
        setForm(data);
    }, [data]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name == 'phone') {
            const regex = /^\d+$/;

            if (name === 'phone') {
                const cleaned = value.replace(/\D+/g, '').slice(0, 10);
                setForm((prev: any) => ({ ...prev, phone: cleaned }));
                return;
            }
        }
        setForm((prev: any) => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        if (form) {
            const filteredData = {
                companyId: form.id || '',
                companyName: form.name || '',
                companyWebsite: form.companyWebsite || '',
                companyCountry: form.country || '',
                companyState: form.state || '',
                companyCity: form.cityDistrict || '',
                companyEmail: form.email || '',
                companyPhone: form.phone || '',
                companyPostalCode: form.postalCode || '',
            };

            onSave(filteredData);
        }
    };

    return (
        <Modal show={show} onHide={() => setShow(false)} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Edit Company</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Company Name</Form.Label>
                        <Form.Control name="name" value={form?.name || ''} onChange={handleInputChange} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Company Email</Form.Label>
                        <Form.Control name="email" value={form?.email || ''} onChange={handleInputChange} />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Contact Number</Form.Label>
                        <Form.Control
                            name="phone"
                            value={form?.phone || ''}
                            maxLength={10}
                            onChange={handleInputChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Company Website</Form.Label>
                        <Form.Control
                            name="companyWebsite"
                            value={form?.companyWebsite || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Country</Form.Label>
                        <CountryDropdown
                            countryFieldValue={form?.country}
                            setIso2CountryString={setIso2CountryString}
                            setCityDisabled={setCityDisabled}
                            setStateDisabled={setStateDisabled}
                            onCountrySelectionChange={(selectedCountry) => {
                                setForm((prev: any) => ({ ...prev, country: selectedCountry.id.toString() }));
                                setForm((prev: any) => ({ ...prev, state: '' }));
                                setForm((prev: any) => ({ ...prev, cityDistrict: '' }));
                                setCityDisabled(true);
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>State</Form.Label>
                        <StateDropdown
                            Iso2CountryString={iso2CountryString}
                            id="state"
                            setIso2StateString={setIso2StateString}
                            stateFieldValue={form?.state}
                            setCityDisabled={setCityDisabled}
                            stateDisabled={stateDisabled}
                            onStateSelectionChange={(selectedState) => {
                                setForm((prev: any) => ({ ...prev, state: selectedState.id.toString() }));
                                setForm((prev: any) => ({ ...prev, cityDistrict: '' }));
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        <CityDropdown
                            id="city"
                            cityFieldValue={form?.cityDistrict}
                            iso2CountryString={iso2CountryString}
                            cityDisabled={cityDisabled}
                            iso2StateString={iso2StateString}
                            onCitySelectionChange={(selectedCity) => {
                                setForm((prev: any) => ({ ...prev, cityDistrict: selectedCity.id.toString() }));
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Postal code</Form.Label>
                        <Form.Control name="postalCode" value={form?.postalCode || ''} onChange={handleInputChange} />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShow(false)}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={
                        loading ||
                        !form.name ||
                        !form.email ||
                        !form.phone ||
                        !form.country ||
                        !form.state ||
                        !form.cityDistrict ||
                        !form.postalCode
                    }
                >
                    {loading && <span className="spinner-border spinner-border-sm" />}
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditCompanyModal;
